import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { logUserToMixpanel } from "../utils/mixpanel";
import { useAPI } from "../hooks/useAPI";
import { checkConnectionQuality } from "../services/connectionTest";

// Non-sensitive, JS-readable session marker. The real auth token lives in an
// HttpOnly cookie the browser attaches automatically (credentials: "include")
// and is NOT readable by JS. This flag carries no credential — it just lets the
// app know synchronously, on first paint, that a session exists, so guards and
// redirects don't have to wait on an async /user round-trip (which caused
// loading flashes and a /login <-> /dashboard loop after the 2FA reload).
const SESSION_FLAG = "gn_session";

const hasSession = () => !!Cookies.get(SESSION_FLAG);

const AuthContext = createContext(null);

export const AuthProvider = ({ children, enabled = true }) => {
  const queryClient = useQueryClient();
  const { callAPI } = useAPI();

  // Initialise synchronously from the readable session flag so the value is
  // truthy on the very first render after a page load / 2FA reload.
  const [authToken, setAuthToken] = useState(() =>
    hasSession() ? "cookie-session" : ""
  );

  // Helper to determine 2FA status
  const is2FA =
    typeof window !== "undefined" &&
    window.location.pathname.toLowerCase().includes("2fa");

  // 2. Define the Query for Fetching User (authenticated by the HttpOnly cookie)
  const {
    data: loggedInUser = {},
    isLoading,
    error,
    refetch: fetchUser,
  } = useQuery({
    queryKey: ["user"],
    queryFn: async () => {
      // Trailing slash: /v1/user is a DRF router endpoint whose canonical URL is
      // /v1/user/. Without the slash Django 301-redirects, and Chrome refuses to
      // follow a redirect on a credentialed, preflighted CORS request → "CORS
      // error" (Firefox tolerates it — hence the Chrome-only symptom).
      const userData = await callAPI(`user/`);

      if (!Object.keys(userData).includes("email")) {
        throw new Error("Invalid user");
      }
      return userData;
    },
    // Only run if we believe a session exists, we aren't in 2FA, and enabled.
    enabled: !!authToken && !is2FA && enabled,
    retry: false, // Don't retry if auth fails (usually means 401)
    staleTime: 1000 * 60 * 5, // Cache user data for 5 minutes
  });

  // 3. Side Effect: Logging to Mixpanel
  useEffect(() => {
    if (loggedInUser && loggedInUser.email) {
      logUserToMixpanel(loggedInUser);
    }
  }, [loggedInUser]);

  // If /user fails (e.g. the HttpOnly cookie expired server-side) the session
  // flag is stale. Clear it so the route guards redirect to /login instead of
  // looping (guard sees a flag → renders → /user 401 → must drop the flag).
  useEffect(() => {
    if (error && authToken) {
      Cookies.remove(SESSION_FLAG);
      setAuthToken("");
    }
  }, [error, authToken]);

  // 4. Connection quality check — runs once when the user is authenticated.
  useEffect(() => {
    if (!authToken) return;
    const path = window.location.pathname.toLowerCase();
    if (path.includes("2fa") || path.startsWith("/login")) return;
    checkConnectionQuality(callAPI, authToken).catch(() => {});
  }, [authToken]);

  // 5. Save session. The backend has already set the HttpOnly token cookie on the
  // login/2FA response; here we just raise the readable session flag (so the app
  // knows a session exists on the next render/reload) and refetch the user.
  // NB: this only ever *establishes* a session. To clear one, call logout() —
  // do not call this expecting it to log the user out.
  const saveAuthToken = useCallback(
    (rememberMe = false) => {
      const isSecure = window.location.protocol === "https:";
      Cookies.set(SESSION_FLAG, "1", {
        ...(rememberMe ? { expires: 30 } : {}),
        secure: isSecure,
        sameSite: "Lax",
      });
      setAuthToken("cookie-session");
      // Cancel any in-flight "user" fetch so a request started before the new
      // HttpOnly cookie landed can't resolve/401 mid-swap and bounce us to /login.
      queryClient.cancelQueries({ queryKey: ["user"] });

      // Refresh user data, but DEFER it so the new HttpOnly cookie has propagated
      // first. A short delay lets the backend's Set-Cookie settle before the
      // refetch, so /user authenticates with the new session rather than the old.
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["user"] });
      }, 200);
    },
    [queryClient]
  );

  // Logout: tell the backend to delete the token + expire the HttpOnly cookie,
  // drop the readable flag, and clear client state.
  const logout = useCallback(() => {
    // Best-effort: revoke the token server-side so it can't be reused after
    // logout. Fire-and-forget — client-side cleanup must always proceed even
    // if the request fails (offline, expired token, etc.).
    if (authToken) {
      callAPI("logout", { method: "POST", token: authToken }).catch(() => {});
    }
    Cookies.remove(SESSION_FLAG);
    setAuthToken("");
    queryClient.clear();
    // Remove the persisted React Query cache so stale data isn't restored on
    // the next login (key is set in main.jsx PersistQueryClientProvider).
    localStorage.removeItem("GURUNOW_REACT_QUERY_CACHE");
    localStorage.removeItem("gn_conn_test");
    sessionStorage.clear();
  }, [queryClient, authToken, callAPI]);



  const value = {
    authToken,
    saveAuthToken,
    loggedInUser,
    fetchUser,
    isLoading,
    error,
    logout,
    isAuthenticated: !!authToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom Hook to consume the context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
